import React, { useState, useRef, useEffect } from 'react';

/**
 * OTP EMAIL FORMATTER - PART 6
 *
 * Format nice HTML email templates
 * See EVERYTHING about email rendering
 */

interface FormatterEvent {
  id: string;
  timestamp: number;
  layer: 'template' | 'style' | 'render' | 'compatibility' | 'preview' | 'optimization';
  type: 'info' | 'success' | 'error' | 'process' | 'detail' | 'warning';
  message: string;
  details?: any;
  duration?: number;
}

interface EmailTemplate {
  id: string;
  name: string;
  type: 'plain' | 'basic' | 'styled' | 'responsive';
  size: number;
  compatibility: string[];
  features: string[];
}

export default function OTPEmailFormatter() {
  const [events, setEvents] = useState<FormatterEvent[]>([]);
  const [isFormatting, setIsFormatting] = useState(false);
  const [otpCode, setOtpCode] = useState('123456');
  const [selectedTemplate, setSelectedTemplate] = useState<'plain' | 'basic' | 'styled' | 'responsive'>('basic');
  const [showSource, setShowSource] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [emailStats, setEmailStats] = useState<any>(null);
  const eventsEndRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const logEvent = (event: Omit<FormatterEvent, 'id' | 'timestamp'>) => {
    const newEvent: FormatterEvent = {
      ...event,
      id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    };

    setEvents(prev => [...prev, newEvent]);
    return new Promise(resolve => setTimeout(resolve, event.duration || 50));
  };

  const generatePlainTemplate = (code: string) => {
    return `Your FutrBet login code is: ${code}

This code expires in 10 minutes.

If you didn't request this, please ignore this email.`;
  };

  const generateBasicTemplate = (code: string) => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Your Login Code</title>
</head>
<body style="font-family: sans-serif; padding: 20px;">
  <h2>Your FutrBet Login Code</h2>
  <div style="background: #f0f0f0; padding: 20px; border-radius: 8px; text-align: center;">
    <h1 style="font-size: 36px; letter-spacing: 8px; margin: 0;">${code}</h1>
  </div>
  <p>This code expires in 10 minutes.</p>
  <p style="color: #666; font-size: 12px;">
    If you didn't request this code, please ignore this email.
  </p>
</body>
</html>`;
  };

  const generateStyledTemplate = (code: string) => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Your Login Code</title>
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
</head>
<body style="margin: 0; padding: 0; background-color: #f4f4f4;">
  <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f4f4f4;">
    <tr>
      <td align="center" style="padding: 40px 0;">
        <table role="presentation" cellpadding="0" cellspacing="0" width="600" style="background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <tr>
            <td style="padding: 40px 30px; text-align: center;">
              <h1 style="margin: 0; color: #333; font-size: 24px;">FutrBet</h1>
            </td>
          </tr>
          <tr>
            <td style="padding: 0 30px 30px;">
              <h2 style="color: #333; font-size: 18px; margin-bottom: 20px;">Your Login Code</h2>
              <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; border-radius: 8px; text-align: center;">
                <div style="font-size: 48px; font-weight: bold; color: #fff; letter-spacing: 12px; font-family: monospace;">
                  ${code}
                </div>
              </div>
              <p style="color: #666; margin-top: 20px;">This code expires in <strong>10 minutes</strong>.</p>
              <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;">
              <p style="color: #999; font-size: 12px; text-align: center;">
                If you didn't request this code, you can safely ignore this email.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const generateResponsiveTemplate = (code: string) => {
    return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="x-apple-disable-message-reformatting">
  <title>Your Login Code</title>
  <style>
    @media only screen and (max-width: 600px) {
      .container { width: 100% !important; }
      .content { padding: 20px !important; }
      .code-display { font-size: 32px !important; letter-spacing: 8px !important; }
    }
    @media (prefers-color-scheme: dark) {
      body { background-color: #1a1a1a !important; }
      .container { background-color: #2a2a2a !important; }
      h1, h2, p { color: #e0e0e0 !important; }
      .code-box { background: linear-gradient(135deg, #4a5568 0%, #2d3748 100%) !important; }
    }
  </style>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f7fafc;">
  <div style="display: none; max-height: 0; overflow: hidden;">
    Your FutrBet login code is ${code}
  </div>

  <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="min-height: 100vh; background-color: #f7fafc;">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table class="container" role="presentation" cellpadding="0" cellspacing="0" width="600" style="max-width: 600px; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,0.1);">
          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%); padding: 40px 30px; text-align: center;">
              <h1 style="margin: 0; color: #ffffff; font-size: 32px; font-weight: 700;">⚡ FutrBet</h1>
              <p style="margin: 10px 0 0; color: rgba(255,255,255,0.9); font-size: 14px;">Secure Authentication</p>
            </td>
          </tr>

          <!-- Content -->
          <tr>
            <td class="content" style="padding: 40px 30px;">
              <h2 style="color: #2d3748; font-size: 20px; margin-bottom: 10px;">Your One-Time Code</h2>
              <p style="color: #718096; margin-bottom: 30px;">Enter this code to complete your login:</p>

              <!-- Code Display -->
              <div class="code-box" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 20px; border-radius: 12px; text-align: center; margin-bottom: 30px;">
                <div class="code-display" style="font-size: 42px; font-weight: 900; color: #ffffff; letter-spacing: 10px; font-family: 'Courier New', monospace; text-shadow: 0 2px 4px rgba(0,0,0,0.2);">
                  ${code}
                </div>
              </div>

              <!-- Timer -->
              <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom: 30px;">
                <tr>
                  <td align="center">
                    <div style="display: inline-block; background-color: #fef5e7; border: 2px solid #f39c12; border-radius: 8px; padding: 12px 24px;">
                      <p style="margin: 0; color: #e67e22; font-weight: 600;">
                        ⏱️ Expires in 10 minutes
                      </p>
                    </div>
                  </td>
                </tr>
              </table>

              <!-- Security Note -->
              <div style="background-color: #f7fafc; border-left: 4px solid #4299e1; padding: 15px; margin-bottom: 30px;">
                <p style="margin: 0; color: #2b6cb1; font-size: 14px;">
                  <strong>🔒 Security Tip:</strong> Never share this code with anyone. FutrBet staff will never ask for your code.
                </p>
              </div>

              <!-- Footer -->
              <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 30px 0;">

              <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
                <tr>
                  <td style="text-align: center;">
                    <p style="color: #a0aec0; font-size: 12px; margin-bottom: 5px;">
                      Didn't request this code? You can safely ignore this email.
                    </p>
                    <p style="color: #a0aec0; font-size: 12px; margin: 0;">
                      © 2024 FutrBet. All rights reserved.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const formatEmail = async () => {
    setIsFormatting(true);
    setEvents([]);
    const startTime = Date.now();

    try {
      // Step 1: Template selection
      await logEvent({
        layer: 'template',
        type: 'info',
        message: 'Selecting email template',
        details: {
          template: selectedTemplate,
          features: {
            plain: 'Text only, universal compatibility',
            basic: 'Simple HTML, basic styling',
            styled: 'Tables for Outlook, inline CSS',
            responsive: 'Media queries, dark mode support'
          }[selectedTemplate]
        }
      });

      // Step 2: Generate HTML
      let html = '';
      switch(selectedTemplate) {
        case 'plain':
          html = generatePlainTemplate(otpCode);
          break;
        case 'basic':
          html = generateBasicTemplate(otpCode);
          break;
        case 'styled':
          html = generateStyledTemplate(otpCode);
          break;
        case 'responsive':
          html = generateResponsiveTemplate(otpCode);
          break;
      }

      const size = new Blob([html]).size;

      await logEvent({
        layer: 'render',
        type: 'process',
        message: 'Generating HTML template',
        details: {
          template: selectedTemplate,
          htmlSize: `${size} bytes`,
          elements: html.match(/<[^>]+>/g)?.length || 0,
          inlineStyles: html.match(/style="/g)?.length || 0
        },
        duration: 100
      });

      // Step 3: CSS inlining
      if (selectedTemplate !== 'plain') {
        await logEvent({
          layer: 'style',
          type: 'process',
          message: 'Inlining CSS for compatibility',
          details: {
            reason: 'Many email clients strip <style> tags',
            method: 'inline-style attributes',
            specificity: 'Important for Gmail'
          },
          duration: 75
        });
      }

      // Step 4: Compatibility checks
      const compatibilityChecks = [
        { client: 'Gmail', support: '✅ Full', notes: 'Inline styles required' },
        { client: 'Outlook', support: '✅ Full', notes: 'Table-based layout' },
        { client: 'Apple Mail', support: '✅ Full', notes: 'Best CSS support' },
        { client: 'Yahoo Mail', support: '✅ Full', notes: 'Good HTML support' },
        { client: 'Outlook.com', support: '⚠️ Partial', notes: 'Limited CSS' }
      ];

      await logEvent({
        layer: 'compatibility',
        type: 'info',
        message: 'Email client compatibility',
        details: {
          template: selectedTemplate,
          clients: compatibilityChecks,
          darkModeSupport: selectedTemplate === 'responsive'
        },
        duration: 100
      });

      // Step 5: Optimization
      await logEvent({
        layer: 'optimization',
        type: 'process',
        message: 'Optimizing for delivery',
        details: {
          originalSize: `${size} bytes`,
          optimizations: [
            'Remove comments',
            'Minify whitespace',
            'Optimize images (none)',
            'Preheader text added'
          ],
          finalSize: `${Math.floor(size * 0.9)} bytes`
        }
      });

      // Step 6: Preview generation
      await logEvent({
        layer: 'preview',
        type: 'process',
        message: 'Generating preview',
        details: {
          desktop: '600px width',
          mobile: '320px width',
          darkMode: selectedTemplate === 'responsive' ? 'Supported' : 'Not supported',
          preheaderText: `Your FutrBet login code is ${otpCode}`
        },
        duration: 150
      });

      // Update preview
      if (previewRef.current) {
        const doc = previewRef.current.contentDocument;
        if (doc) {
          doc.open();
          doc.write(html);
          doc.close();
        }
      }

      // Step 7: Validation
      await logEvent({
        layer: 'template',
        type: 'success',
        message: '✅ EMAIL FORMATTED',
        details: {
          template: selectedTemplate,
          code: otpCode,
          size: `${size} bytes`,
          renderTime: `${Date.now() - startTime}ms`,
          ready: true
        }
      });

      setEmailStats({
        size,
        template: selectedTemplate,
        elements: html.match(/<[^>]+>/g)?.length || 0,
        compatibility: compatibilityChecks.filter(c => c.support.includes('✅')).length
      });

    } catch (error) {
      await logEvent({
        layer: 'render',
        type: 'error',
        message: 'Formatting failed',
        details: { error: error instanceof Error ? error.message : 'Unknown error' }
      });
    } finally {
      setIsFormatting(false);
    }
  };

  const testAllTemplates = async () => {
    setEvents([]);
    const templates: Array<'plain' | 'basic' | 'styled' | 'responsive'> = ['plain', 'basic', 'styled', 'responsive'];

    for (const template of templates) {
      setSelectedTemplate(template);
      await logEvent({
        layer: 'template',
        type: 'detail',
        message: `Testing ${template} template`,
        details: {
          type: template,
          complexity: templates.indexOf(template) + 1,
          features: {
            plain: ['Text only', 'Universal'],
            basic: ['Simple HTML', 'Inline styles'],
            styled: ['Table layout', 'Gradients'],
            responsive: ['Media queries', 'Dark mode', 'Mobile optimized']
          }[template]
        },
        duration: 100
      });
    }

    await logEvent({
      layer: 'template',
      type: 'success',
      message: 'All templates tested',
      details: {
        count: templates.length,
        recommendation: 'Use "styled" for best compatibility'
      }
    });
  };

  const getLayerColor = (layer: string) => {
    const colors: Record<string, string> = {
      template: 'text-blue-400',
      style: 'text-purple-400',
      render: 'text-yellow-400',
      compatibility: 'text-green-400',
      preview: 'text-cyan-400',
      optimization: 'text-pink-400'
    };
    return colors[layer] || 'text-gray-400';
  };

  const getLayerIcon = (layer: string) => {
    const icons: Record<string, string> = {
      template: '📄',
      style: '🎨',
      render: '🖼️',
      compatibility: '✓',
      preview: '👁️',
      optimization: '⚡'
    };
    return icons[layer] || '•';
  };

  useEffect(() => {
    formatEmail();
  }, [selectedTemplate, otpCode]);

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header */}
        <div className="mb-6 border border-green-400 p-4">
          <h1 className="text-3xl mb-2">🎨 OTP EMAIL FORMATTER - PART 6</h1>
          <p className="text-gray-400">Format nice HTML email templates</p>
          <p className="text-yellow-400 text-sm mt-2">From plain text to responsive design</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Left: Controls and Preview */}
          <div className="space-y-4">
            {/* Template Selection */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-green-400">📝 TEMPLATE</h2>

              <div className="space-y-3">
                <div>
                  <label className="text-gray-400 text-sm">OTP Code</label>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.slice(0, 6))}
                    className="w-full bg-gray-900 border border-gray-600 p-2 text-green-400 mt-1 text-center text-2xl tracking-widest"
                    placeholder="123456"
                    maxLength={6}
                  />
                </div>

                <div className="space-y-2">
                  {(['plain', 'basic', 'styled', 'responsive'] as const).map(template => (
                    <button
                      key={template}
                      onClick={() => setSelectedTemplate(template)}
                      className={`w-full p-3 border text-left ${
                        selectedTemplate === template
                          ? 'border-green-400 bg-green-900/20 text-green-400'
                          : 'border-gray-600 text-gray-400 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-bold capitalize">{template}</div>
                      <div className="text-xs mt-1">
                        {template === 'plain' && 'Text only, works everywhere'}
                        {template === 'basic' && 'Simple HTML with inline styles'}
                        {template === 'styled' && 'Tables for Outlook, gradients'}
                        {template === 'responsive' && 'Media queries, dark mode'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4">🎮 CONTROLS</h2>

              <div className="space-y-2">
                <button
                  onClick={formatEmail}
                  disabled={isFormatting}
                  className="w-full bg-green-400 text-black p-3 hover:bg-green-300 disabled:opacity-50 font-bold"
                >
                  {isFormatting ? '🎨 FORMATTING...' : '🎨 FORMAT EMAIL'}
                </button>

                <button
                  onClick={testAllTemplates}
                  className="w-full bg-blue-400 text-black p-3 hover:bg-blue-300"
                >
                  🧪 TEST ALL TEMPLATES
                </button>

                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="w-full bg-purple-400 text-black p-3 hover:bg-purple-300"
                >
                  {showPreview ? '🚫 HIDE PREVIEW' : '👁️ SHOW PREVIEW'}
                </button>

                <button
                  onClick={() => setShowSource(!showSource)}
                  className="w-full bg-yellow-400 text-black p-3 hover:bg-yellow-300"
                >
                  {showSource ? '🚫 HIDE SOURCE' : '📄 SHOW SOURCE'}
                </button>

                <button
                  onClick={() => setEvents([])}
                  className="w-full bg-gray-700 text-white p-3 hover:bg-gray-600"
                >
                  🔄 CLEAR EVENTS
                </button>
              </div>
            </div>

            {/* Stats */}
            {emailStats && (
              <div className="border border-cyan-400 p-4">
                <h2 className="text-xl mb-4 text-cyan-400">📊 EMAIL STATS</h2>
                <div className="text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Template:</span>
                    <span className="capitalize">{emailStats.template}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Size:</span>
                    <span>{emailStats.size} bytes</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">HTML Elements:</span>
                    <span>{emailStats.elements}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Compatible Clients:</span>
                    <span>{emailStats.compatibility}/5</span>
                  </div>
                </div>
              </div>
            )}

            {/* Preview */}
            {showPreview && (
              <div className="border border-purple-400 p-4">
                <h2 className="text-xl mb-4 text-purple-400">📧 EMAIL PREVIEW</h2>
                <div className="bg-white rounded" style={{ height: '400px' }}>
                  <iframe
                    ref={previewRef}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    title="Email Preview"
                  />
                </div>
              </div>
            )}

            {/* Source */}
            {showSource && (
              <div className="border border-yellow-400 p-4">
                <h2 className="text-xl mb-4 text-yellow-400">📄 HTML SOURCE</h2>
                <pre className="text-xs text-gray-400 overflow-x-auto max-h-64 overflow-y-auto">
                  {selectedTemplate === 'plain' ? generatePlainTemplate(otpCode) :
                   selectedTemplate === 'basic' ? generateBasicTemplate(otpCode) :
                   selectedTemplate === 'styled' ? generateStyledTemplate(otpCode) :
                   generateResponsiveTemplate(otpCode)}
                </pre>
              </div>
            )}
          </div>

          {/* Right: Event Stream */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">📡 FORMATTER EVENT STREAM</h2>

            <div className="h-[700px] overflow-y-auto space-y-2 text-xs font-mono">
              {events.length === 0 ? (
                <div className="text-gray-600 text-center py-8">
                  Waiting for formatting events...
                </div>
              ) : (
                events.map(event => (
                  <div
                    key={event.id}
                    className={`border-l-2 pl-2 ${
                      event.type === 'success' ? 'border-green-400' :
                      event.type === 'error' ? 'border-red-400' :
                      event.type === 'warning' ? 'border-orange-400' :
                      event.type === 'process' ? 'border-blue-400' :
                      event.type === 'detail' ? 'border-purple-400' :
                      'border-gray-400'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{getLayerIcon(event.layer)}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">
                            [{new Date(event.timestamp).toLocaleTimeString('en-US', {
                              hour12: false,
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              fractionalSecondDigits: 3
                            })}]
                          </span>
                          <span className={`font-bold ${getLayerColor(event.layer)}`}>
                            {event.layer.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-gray-300 mt-1">{event.message}</div>
                        {event.details && (
                          <pre className="text-gray-500 mt-2 bg-gray-900 p-2 rounded overflow-x-auto">
{JSON.stringify(event.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={eventsEndRef} />
            </div>
          </div>
        </div>

        {/* Bottom: Complete Picture */}
        {emailStats && (
          <div className="mt-6 border border-cyan-400 p-4">
            <h2 className="text-xl text-cyan-400 mb-2">🎯 FORMATTING COMPLETE!</h2>
            <p className="text-gray-400 mb-4">
              Your OTP email has been formatted with {selectedTemplate} template,
              optimized for {emailStats.compatibility} major email clients.
            </p>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-400 font-bold">Template Layer:</span>
                <div className="text-gray-400">
                  • Structure selection<br />
                  • Layout choice<br />
                  • Feature set
                </div>
              </div>
              <div>
                <span className="text-purple-400 font-bold">Style Layer:</span>
                <div className="text-gray-400">
                  • CSS inlining<br />
                  • Table layouts<br />
                  • Dark mode
                </div>
              </div>
              <div>
                <span className="text-green-400 font-bold">Compatibility:</span>
                <div className="text-gray-400">
                  • Gmail support<br />
                  • Outlook tables<br />
                  • Mobile responsive
                </div>
              </div>
              <div>
                <span className="text-pink-400 font-bold">Optimization:</span>
                <div className="text-gray-400">
                  • Size reduction<br />
                  • Load speed<br />
                  • Spam score
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}